/**
 * Парсинг сообщений AI для извлечения блоков кода
 * Поддерживает форматы: [demo:sql_code] и [ai-demo:sql_code]
 */

import { format } from "sql-formatter";
import { splitSqlStatements, joinSqlStatements } from "./formatSqlMulti";

/** Маркер для незавершённого блока [demo:...] при streaming — показывается как placeholder */
const DEMO_PLACEHOLDER_MARKER = "__DEMO_PLACEHOLDER__";

/** Регулярка для [SQL_PLACEHOLDER: type=..., description="..."] */
const SQL_PLACEHOLDER_PATTERN = /\[SQL_PLACEHOLDER:[^\]]*\]/gi;
/** Регулярка для [GENERATE_SQL] или [GENERATE_SQL: описание] */
const GENERATE_SQL_PATTERN = /\[GENERATE_SQL(?::\s*[^\]]*)?\]/gi;

/**
 * Единая функция маскировки стримингового контента.
 *
 * Заменяет ВСЕ формы SQL-кода на единственный placeholder `[demo:__DEMO_PLACEHOLDER__]`.
 * Гарантирует: ровно 0 или 1 placeholder в результате (не больше).
 *
 * Обрабатывает (в порядке приоритета):
 *  1. [SQL_PLACEHOLDER: ...] — основной маркер от LLM (полный и частичный)
 *  2. [GENERATE_SQL: ...] — legacy маркер
 *  3. ```sql...``` — fenced-блоки (LLM иногда игнорирует запрет)
 *  4. [demo-run:...] / [demo-show:...] / [demo:...] / [ai-demo:...] — незакрытые стриминговые блоки
 *  5. Голый SQL в начале (SELECT..., CREATE...) — если нет других маркеров
 *
 * Принцип: один проход, один placeholder на выходе. Текст вокруг сохраняется.
 */
export function maskStreamingContent(content: string): string {
  if (!content) return content;

  const placeholder = `[demo:${DEMO_PLACEHOLDER_MARKER}]`;
  let result = content;

  // ── 1. [SQL_PLACEHOLDER: ...] — полные вхождения ──
  result = result.replace(SQL_PLACEHOLDER_PATTERN, placeholder);

  // ── 1b. Частичный [SQL_PLACEHOLDER без закрывающей ] ──
  const idxPartialWithColon = result.indexOf("[SQL_PLACEHOLDER:");
  if (idxPartialWithColon !== -1) {
    result = result.substring(0, idxPartialWithColon) + placeholder;
  }

  // ── 1c. Совсем ранний partial: [SQL_ без двоеточия (граница чанка) ──
  const idxSqlStub = result.indexOf("[SQL_");
  if (idxSqlStub !== -1) {
    result = result.substring(0, idxSqlStub) + placeholder;
  }

  // ── 2. [GENERATE_SQL: ...] — legacy ──
  result = result.replace(GENERATE_SQL_PATTERN, placeholder);

  // ── 3. ```sql...``` fenced-блоки ──
  // Удаляем полностью закрытые
  result = result.replace(/```(?:sql|clickhouse|ch)\b[^\n]*\n[\s\S]*?```/gi, "");
  // Незакрытый fenced-блок в конце → один placeholder
  const openFencedIdx = result.search(/```(?:sql|clickhouse|ch)\b/i);
  if (openFencedIdx !== -1) {
    result = result.substring(0, openFencedIdx) + placeholder;
  }

  // ── 4. Незакрытые [demo:...] блоки ──
  const DEMO_PREFIXES = ["[demo-run:", "[demo-show:", "[demo:", "[ai-demo:"];
  for (const prefix of DEMO_PREFIXES) {
    let searchFrom = 0;
    while (true) {
      const idx = result.indexOf(prefix, searchFrom);
      if (idx === -1) break;

      // Ищем закрывающую ] с учётом вложенности
      let depth = 0;
      let blockEnd = -1;
      for (let i = idx; i < result.length; i++) {
        const ch = result[i];
        if (ch === "'" || ch === '"') {
          const quote = ch;
          i++;
          while (i < result.length) {
            if (result[i] === "\\") i++;
            else if (result[i] === quote) break;
            i++;
          }
        } else if (ch === "[") {
          depth++;
        } else if (ch === "]") {
          depth--;
          if (depth === 0) { blockEnd = i; break; }
        }
      }

      if (blockEnd !== -1) {
        // Блок закрыт — пропускаем
        searchFrom = blockEnd + 1;
      } else {
        // Блок не закрыт — маскируем хвост
        result = result.substring(0, idx) + `[${prefix.slice(1, -1)}:${DEMO_PLACEHOLDER_MARKER}]`;
        break;
      }
    }
  }

  // ── 5. Голый SQL в начале (если нет других маркеров) ──
  const hasDemoMarker = result.includes("[demo:") || result.includes("[ai-demo:") ||
    result.includes("[demo-run:") || result.includes("[demo-show:");
  if (!hasDemoMarker) {
    const trimmed = result.trim();
    if (/^(SELECT|WITH|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|EXPLAIN)\b/i.test(trimmed)) {
      result = placeholder;
    }
  }

  return result;
}

// ── Exported aliases for backward compatibility (used in AITutorMessageContent.tsx) ──
// All delegate to maskStreamingContent — the unified single-pass function.
export const maskSqlPlaceholderWhenStreaming = maskStreamingContent;
export const maskLeadingRawSqlWhenStreaming = (c: string) => c;
export const maskStreamingDemoBlocks = (c: string) => c;
export const maskFencedSqlWhenStreaming = (c: string) => c;
export const maskGenerateSqlWhenStreaming = (c: string) => c;

export function isDemoPlaceholder(sqlCode: string | undefined): boolean {
  return sqlCode === DEMO_PLACEHOLDER_MARKER;
}

export interface ParsedMessagePart {
  type: "text" | "code";
  content?: string;
  sqlCode?: string;
  /** Тип SQL блока: runnable (можно запустить) или illustrative (только читать) */
  sqlType?: "runnable" | "illustrative";
}

/**
 * Форматирует один SQL-оператор с переносами строк.
 * Pre-process: скрываем ClickHouse-специфичные литералы (массивы, типы) перед форматтером,
 * затем восстанавливаем. Если форматтер всё равно падает — возвращаем SQL как есть.
 */
function formatSqlSingleStatement(trimmed: string): string {
  const placeholders: string[] = [];
  const sanitized = trimmed
    .replace(/\[(?:[^\[\]]|\[[^\[\]]*\])*\]/g, (m) => {
      placeholders.push(m);
      return `'__CH_${placeholders.length - 1}__'`;
    })
    .replace(/\b(Array|LowCardinality|FixedString|Nullable|AggregateFunction|SimpleAggregateFunction)\s*\(/g, (m) => {
      placeholders.push(m);
      return `__CHTYPE_${placeholders.length - 1}__(`;
    });

  try {
    let formatted = format(sanitized, {
      language: "sql",
      tabWidth: 2,
      useTabs: false,
      keywordCase: "upper",
      indentStyle: "standard",
    });
    formatted = formatted.replace(/\b([A-Za-z_][A-Za-z0-9_]*)\s+\(/g, "$1(");
    formatted = formatted.replace(/'__CH_(\d+)__'/g, (_, i) => placeholders[+i] ?? _);
    formatted = formatted.replace(/__CHTYPE_(\d+)__\(/g, (_, i) => placeholders[+i] ?? `__CHTYPE_${i}__(`);
    return formatted;
  } catch {
    return trimmed;
  }
}

/**
 * Форматирует SQL (в т.ч. несколько операторов через `;`): каждый оператор отдельно,
 * между ними — дополнительная пустая строка (`;\n\n`).
 */
function formatSqlCode(sql: string): string {
  if (!sql || typeof sql !== "string") return sql;
  const trimmed = sql.trim();
  const hadTrailingSemicolon = /;\s*$/.test(trimmed);
  const parts = splitSqlStatements(trimmed);
  if (parts.length === 0) return trimmed;
  if (parts.length === 1) {
    return formatSqlSingleStatement(parts[0]);
  }
  const formatted = parts.map((p) => formatSqlSingleStatement(p));
  return joinSqlStatements(formatted, hadTrailingSemicolon);
}

/** Все поддерживаемые префиксы блоков кода и их тип */
const CODE_BLOCK_PREFIXES: Array<{ prefix: string; sqlType: "runnable" | "illustrative" }> = [
  { prefix: "[demo-run:", sqlType: "runnable" },
  { prefix: "[demo-show:", sqlType: "illustrative" },
  { prefix: "[demo:", sqlType: "runnable" },       // backward compat
  { prefix: "[ai-demo:", sqlType: "runnable" },    // backward compat
];

function findNextCodeBlock(
  content: string,
  fromIndex: number
): { blockStart: number; prefix: string; sqlType: "runnable" | "illustrative" } | null {
  let best: { blockStart: number; prefix: string; sqlType: "runnable" | "illustrative" } | null =
    null;
  for (const { prefix, sqlType } of CODE_BLOCK_PREFIXES) {
    const idx = content.indexOf(prefix, fromIndex);
    if (idx !== -1 && (best === null || idx < best.blockStart)) {
      best = { blockStart: idx, prefix, sqlType };
    }
  }
  return best;
}

/**
 * После извлечения code-блока пропускает сырой SQL, который LLM продублировал как текст.
 * LLM иногда ставит [SQL_PLACEHOLDER:] И пишет SQL напрямую сразу после — нужно удалить дубль.
 * Эвристика: если после code-блока идут строки без кириллицы, начинающиеся с SQL-ключевого слова,
 * пропускаем их. Как только встретим кириллицу или markdown-структуру — останавливаемся.
 */
function skipLeakedRawSql(content: string, fromIndex: number): number {
  // Пропускаем начальные пробелы/переносы
  let i = fromIndex;
  while (i < content.length && (content[i] === "\n" || content[i] === "\r" || content[i] === " " || content[i] === "\t")) i++;
  if (i >= content.length) return fromIndex;

  // Первая непустая строка должна начинаться с SQL-ключевого слова
  const rest = content.substring(i);
  if (!/^(SELECT|WITH|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|EXPLAIN)\b/i.test(rest)) {
    return fromIndex;
  }

  // Сканируем построчно: пропускаем строки без кириллицы (это SQL)
  let pos = i;
  while (pos < content.length) {
    let lineEnd = content.indexOf("\n", pos);
    if (lineEnd === -1) lineEnd = content.length;
    const line = content.substring(pos, lineEnd).trim();

    if (line === "") {
      pos = lineEnd + 1;
      continue;
    }

    // Кириллица → текст ответа, останавливаемся
    if (/[а-яА-ЯёЁ]/.test(line)) break;
    // Начало нового code-блока → останавливаемся
    if (/^\[(?:demo|ai-demo|demo-run|demo-show|SQL_)/.test(line)) break;

    // Строка без кириллицы после code-блока → вероятно вытекший SQL, пропускаем
    pos = lineEnd + 1;
  }

  return pos > i ? pos : fromIndex;
}

export function parseAIMessage(content: string): ParsedMessagePart[] {
  const result: ParsedMessagePart[] = [];
  let currentIndex = 0;

  while (currentIndex < content.length) {
    const found = findNextCodeBlock(content, currentIndex);

    if (!found) {
      if (currentIndex < content.length) {
        result.push({ type: "text", content: content.substring(currentIndex) });
      }
      break;
    }

    const { blockStart, prefix, sqlType } = found;

    // Добавляем текст до блока
    if (blockStart > currentIndex) {
      result.push({ type: "text", content: content.substring(currentIndex, blockStart) });
    }

    // Ищем закрывающую ] с учётом вложенности (SQL может содержать массивы ['a','b'])
    // Строковые литералы ('...' и "...") пропускаем — ] внутри них не считается.
    let depth = 0;
    let blockEnd = -1;
    for (let i = blockStart; i < content.length; i++) {
      const ch = content[i];
      if (ch === "'" || ch === '"') {
        // Пропускаем содержимое строкового литерала
        const quote = ch;
        i++;
        while (i < content.length) {
          if (content[i] === "\\") { i++; } // экранированный символ
          else if (content[i] === quote) break;
          i++;
        }
      } else if (ch === "[") {
        depth++;
      } else if (ch === "]") {
        depth--;
        if (depth === 0) {
          blockEnd = i;
          break;
        }
      }
    }

    // Для [demo-run:] и [demo-show:] SQL уже отформатирован бэкендом через formatQuery()
    const isPreFormatted = prefix === "[demo-run:" || prefix === "[demo-show:";

    // Блок не закрыт (стрим обрезан) — показываем как код до конца
    if (blockEnd === -1) {
      const rawSqlCode = content.substring(blockStart + prefix.length).trim();
      const sqlCode =
        rawSqlCode === DEMO_PLACEHOLDER_MARKER || isPreFormatted
          ? rawSqlCode
          : formatSqlCode(rawSqlCode);
      result.push({ type: "code", sqlCode, sqlType });
      break;
    }

    const rawSqlCode = content.substring(blockStart + prefix.length, blockEnd).trim();
    const sqlCode =
      rawSqlCode === DEMO_PLACEHOLDER_MARKER || isPreFormatted
        ? rawSqlCode
        : formatSqlCode(rawSqlCode);

    result.push({ type: "code", sqlCode, sqlType });
    currentIndex = skipLeakedRawSql(content, blockEnd + 1);
  }

  return result.length > 0 ? result : [{ type: "text", content }];
}

const FOLLOW_UP_BLOCK_START = "[ВОПРОСЫ_ДАЛЬШЕ]";
const FOLLOW_UP_BLOCK_END = "[/ВОПРОСЫ_ДАЛЬШЕ]";

/** Варианты заголовка блока вопросов (модель может вывести без тегов) */
const FOLLOW_UP_HEADING = /Возможные вопросы дальше\s*[:\s]*/i;

/**
 * Парсит список вопросов из одной строки (через "? ") или из нескольких строк.
 */
function parseQuestionsText(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const lines = trimmed.split(/\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length > 1) {
    return lines.map((q) => (q.endsWith("?") ? q : q + "?"));
  }
  return trimmed
    .split(/\?\s+/)
    .map((q) => q.trim())
    .filter(Boolean)
    .map((q) => (q.endsWith("?") ? q : q + "?"));
}

/**
 * Извлекает блок "Возможные вопросы дальше" из ответа AI.
 * Поддерживает формат [ВОПРОСЫ_ДАЛЬШЕ]...[/ВОПРОСЫ_ДАЛЬШЕ] и fallback "Возможные вопросы дальше: ...".
 * Возвращает контент без блока и массив вопросов для отображения кнопками.
 */
export function extractFollowUpQuestions(content: string): {
  contentWithoutBlock: string;
  questions: string[];
} {
  const startIdx = content.indexOf(FOLLOW_UP_BLOCK_START);
  if (startIdx !== -1) {
    const endIdx = content.indexOf(FOLLOW_UP_BLOCK_END, startIdx);
    if (endIdx !== -1) {
      const inner = content
        .slice(startIdx + FOLLOW_UP_BLOCK_START.length, endIdx)
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const contentWithoutBlock =
        content.slice(0, startIdx).trimEnd() +
        (endIdx + FOLLOW_UP_BLOCK_END.length < content.length
          ? content.slice(endIdx + FOLLOW_UP_BLOCK_END.length)
          : "");
      return {
        contentWithoutBlock: contentWithoutBlock.trim(),
        questions: inner.map((q) => (q.endsWith("?") ? q : q + "?")),
      };
    }
  }

  const headingMatch = content.match(FOLLOW_UP_HEADING);
  if (headingMatch) {
    const start = headingMatch.index! + headingMatch[0].length;
    const rest = content.slice(start).trim();
    const questions = parseQuestionsText(rest);
    if (questions.length > 0) {
      const contentWithoutBlock = content.slice(0, headingMatch.index).trimEnd();
      return { contentWithoutBlock, questions };
    }
  }

  return { contentWithoutBlock: content, questions: [] };
}
